import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { X, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { getPendingReferralCode } from "@/hooks/useReferralCapture";

const SESSION_KEY_GENERIC = "kudoz-auth-banner-dismissed";
const SESSION_KEY_REFERRAL = "kudoz-referral-banner-dismissed";

export default function AuthPromptBanner() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();

  const referralCode = getPendingReferralCode();

  // sessionKey depends on whether we have a referral code, but we re-sync
  // dismissal state reactively via the effect below whenever it changes.
  const sessionKey = referralCode ? SESSION_KEY_REFERRAL : SESSION_KEY_GENERIC;

  const [dismissed, setDismissed] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  // null = loading/unknown, false = invalid (show generic), true = valid
  const [referralValid, setReferralValid] = useState<boolean | null>(null);

  // Re-read dismissal from sessionStorage whenever the key changes
  useEffect(() => {
    setDismissed(sessionStorage.getItem(sessionKey) === "true");
  }, [sessionKey]);

  useEffect(() => {
    if (!referralCode || isAuthenticated) return;
    fetch(`/api/referrals/validate/${referralCode}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setReferralValid(true);
          setReferrerName(data.referrerName ?? null);
        } else {
          setReferralValid(false);
        }
      })
      .catch(() => {
        setReferralValid(false);
      });
  }, [referralCode, isAuthenticated]);

  if (isAuthenticated || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(sessionKey, "true");
    setDismissed(true);
  };

  // Only show referral-specific messaging when the code is confirmed valid
  const isReferral = !!referralCode && referralValid === true;

  return (
    <div className="w-full bg-muted/60 border-b border-border/50 px-4 py-2">
      <div className="flex items-center gap-2 max-w-2xl mx-auto">
        {isReferral ? (
          <Users className="h-4 w-4 text-[#0D9C97] shrink-0" />
        ) : (
          <Sparkles className="h-4 w-4 text-[#0D9C97] shrink-0" />
        )}
        <p className="flex-1 text-xs text-muted-foreground leading-snug">
          {isReferral
            ? referrerName
              ? `You've been invited by ${referrerName}! Sign up to join their food network and earn bonus Kudoz.`
              : `You've been invited! Sign up to join their food network and earn bonus Kudoz.`
            : `Sign in to earn Kudoz rewards, save favorites & order faster.`}
        </p>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2.5 text-xs"
            onClick={() => navigate("/login")}
          >
            Sign In
          </Button>
          <Button
            size="sm"
            className="h-7 px-2.5 text-xs bg-[#0D9C97] text-white"
            onClick={() => navigate("/signup")}
          >
            Sign Up
          </Button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
